import { jsPDF } from "jspdf";

const BLUE_PRIMARY = [41, 119, 237];
const BLUE_HEADER_RIGHT = [30, 90, 200];
const DIGI_CYAN = [0, 163, 218];
const XLEADS_GRAY = [58, 76, 93];
const WATERMARK_RGB = [200, 242, 250];

const DIGIXLEADS_ISSUER_LINES = [
  "Hyderabad, Telangana, 500079",
  "+91 (814) 225 - 1234",
  "info@tumma.co"
];

const TUMMA_ISSUER_LINES = [
  "Hyderabad, Telangana, 500079",
  "+91 (814) 225 - 1234",
  "info@tumma.co",
  "https://tumma.co"
];

const sanitize = (value) => {
  if (value == null) return "";
  return String(value).replace(/\r?\n/g, " ").trim();
};

const isDigixIssuer = (issuer, title = "") => {
  const code = String(issuer?.companyCode || issuer?.Company_Code || "").toUpperCase();
  if (code === "DIGIXLEADS" || code === "DIGIX") return true;
  if (code === "TUMMA") return false;
  return /^digi\s*x?\s*leads$/i.test(String(title).replace(/\s+/g, ""));
};

/**
 * @param {object} [issuer]
 * @returns {{ title: string, lines: string[], watermark: string, isDigix: boolean, logoDataUrl?: string }}
 */
const resolveIssuer = (issuer) => {
  if (!issuer) {
    return {
      title: "DigiXLeads",
      lines: [...DIGIXLEADS_ISSUER_LINES],
      watermark: "DIGIXLEADS",
      isDigix: true,
      logoDataUrl: null
    };
  }
  const title = sanitize(
    issuer.displayName || issuer.Display_Name || issuer.companyName || issuer.Company_Name || "Company"
  );
  const code = sanitize(issuer.companyCode || issuer.Company_Code || "").toUpperCase();
  const digix = isDigixIssuer(issuer, title);
  const lines = [
    sanitize(issuer.address || issuer.Address_Line || ""),
    sanitize(issuer.phone || issuer.Phone || ""),
    sanitize(issuer.email || issuer.Email || ""),
    sanitize(issuer.website || issuer.Website || "")
  ].filter(Boolean);

  let fallbackLines = [];
  if (!lines.length) {
    fallbackLines = digix ? [...DIGIXLEADS_ISSUER_LINES] : code === "TUMMA" ? [...TUMMA_ISSUER_LINES] : [];
  }

  const watermarkRaw =
    code ||
    sanitize(issuer.displayName || issuer.Display_Name || issuer.companyName || issuer.Company_Name || "INVOICE");
  const watermark = watermarkRaw.replace(/\s+/g, "").toUpperCase().slice(0, 24) || "INVOICE";

  return {
    title: title || "Company",
    lines: lines.length ? lines : fallbackLines,
    watermark,
    isDigix: digix,
    logoDataUrl: issuer.logoDataUrl || null
  };
};

const drawWatermark = (doc, text = "DIGIXLEADS") => {
  const pageWidth = doc.internal.pageSize.getWidth();
  const pageHeight = doc.internal.pageSize.getHeight();
  const cx = pageWidth / 2;
  const cy = pageHeight / 2;
  const label = sanitize(text) || "INVOICE";
  const fontSize = label.length > 14 ? 42 : label.length > 10 ? 50 : 60;
  doc.setFontSize(fontSize);
  doc.setTextColor(...WATERMARK_RGB);
  doc.setFont("helvetica", "normal");
  doc.text(label, cx + 15, cy, { align: "center", baseline: "middle", angle: 45 });
};

/**
 * Right-aligned party block (name, company, contact, email, phone, address, GSTIN/PAN or
 * international tax id such as EIN / VAT).
 * Returns the y position after the last line.
 */
const drawBilledToLines = (doc, billedTo, x, startY, pageWidth) => {
  let y = startY;
  const line = (text) => {
    doc.text(text, x, y, { align: "right" });
    y += 5;
  };
  const clientCompany = sanitize(billedTo.companyName || billedTo.company || "");
  const billedName = sanitize(billedTo.name || "");
  if (billedName) line(billedName);
  // Organisation snapshots carry the company as the name; don't print it twice.
  if (clientCompany && clientCompany.toLowerCase() !== billedName.toLowerCase()) line(clientCompany);
  if (!billedName && !clientCompany) line("Customer");
  const contact = sanitize(billedTo.contact || "");
  if (contact && contact.toLowerCase() !== billedName.toLowerCase()) line(`Attn: ${contact}`);
  const billedEmail = sanitize(billedTo.email || "");
  if (billedEmail) line(billedEmail);
  const billedPhone = sanitize(billedTo.phone || billedTo.mobile || "");
  if (billedPhone) line(billedPhone);
  const billedAddress = sanitize(billedTo.address || "");
  if (billedAddress) {
    const addrLines = doc.splitTextToSize(billedAddress, pageWidth - 60);
    doc.text(addrLines, x, y, { align: "right" });
    y += addrLines.length * 5;
  }
  const gstin = sanitize(billedTo.gstin || billedTo.GSTIN || "");
  if (gstin) line(`GSTIN: ${gstin.toUpperCase()}`);
  const pan = sanitize(billedTo.pan || billedTo.PAN || "");
  if (pan) line(`PAN: ${pan.toUpperCase()}`);
  const taxId = sanitize(billedTo.taxId || billedTo.Tax_Id || "");
  if (taxId) {
    const taxType = sanitize(billedTo.taxIdType || billedTo.Tax_Id_Type || "") || "Tax ID";
    line(`${taxType}: ${taxId.toUpperCase()}`);
  }
  return y;
};

const formatIndianNumber = (value) => {
  const n = Number(value || 0);
  const fixed = n.toFixed(2);
  const [intPart, decPart] = fixed.split(".");
  const s = String(Math.floor(Math.abs(Number(intPart))));
  let grouped = s;
  if (s.length > 3) {
    const last3 = s.slice(-3);
    let rest = s.slice(0, -3);
    const parts = [];
    while (rest.length > 2) {
      parts.unshift(rest.slice(-2));
      rest = rest.slice(0, -2);
    }
    if (rest) parts.unshift(rest);
    grouped = `${parts.join(",")},${last3}`;
  }
  return `${n < 0 ? "-" : ""}${grouped}.${decPart}`;
};

/**
 * Invoice PDF — DigixLeads styling aligned with receipt/quote PDFs.
 * @param {object} data
 * @param {object} data.invoice - Invoice_No, Invoice_Date, Due_Date, billingPeriod, Notes, totals
 * @param {object} data.billedTo - name, company, contact, email, phone, address, gstin, pan, taxIdType, taxId
 * @param {array} data.lineItems - { item, quantity, rate, amount }
 * @param {array} [data.payments] - { date, amount, mode, ref }
 */
export function generateInvoicePdf(data) {
  const {
    invoice = {},
    billedTo = {},
    lineItems = [],
    payments = [],
    issuer: issuerInput = null
  } = data || {};

  const doc = new jsPDF();
  const pageWidth = doc.internal.pageSize.getWidth();
  const pageHeight = doc.internal.pageSize.getHeight();
  const tableMargin = 20;
  const tableRight = pageWidth - tableMargin;
  const issuerInfo = resolveIssuer(issuerInput);
  const wm = issuerInfo.watermark;

  drawWatermark(doc, wm);

  const defaultTextStyle = () => {
    doc.setFontSize(10);
    doc.setFont("helvetica", "normal");
    doc.setTextColor(0, 0, 0);
  };
  const headingStyle = () => {
    doc.setFontSize(11);
    doc.setFont("helvetica", "bold");
    doc.setTextColor(0, 0, 0);
  };

  let yPos = 20;
  defaultTextStyle();

  doc.setFontSize(14);
  doc.setFont("helvetica", "bold");
  doc.setTextColor(0, 0, 0);
  doc.text("INVOICE", tableMargin, yPos);
  defaultTextStyle();

  const invoiceNo = sanitize(invoice.invoiceNo || invoice.Invoice_No || "");
  const invoiceDate = sanitize(invoice.invoiceDate || invoice.Invoice_Date || "");
  const dueDate = sanitize(invoice.dueDate || invoice.Due_Date || "");
  const billingPeriod = sanitize(invoice.billingPeriod || "");
  const currency = sanitize(invoice.currency || invoice.Currency_Code || "");

  const metaFields = [
    invoiceNo ? `No: ${invoiceNo}` : "",
    invoiceDate ? `Date: ${invoiceDate}` : "",
    billingPeriod ? `Period: ${billingPeriod}` : "",
    dueDate ? `Due: ${dueDate}` : "",
    currency ? `Currency: ${currency}` : ""
  ].filter(Boolean);

  // Two columns on the right to save vertical space:
  //   No: …        Date: …
  //   Period: …    Due: …
  //   Currency: …
  const metaColGap = 6;
  const metaColWidth = 52;
  const metaRightX = tableRight;
  const metaLeftX = tableRight - metaColWidth - metaColGap;
  let metaY = yPos;
  for (let i = 0; i < metaFields.length; i += 2) {
    const left = metaFields[i];
    const right = metaFields[i + 1];
    if (right) {
      doc.text(left, metaLeftX, metaY, { align: "right" });
      doc.text(right, metaRightX, metaY, { align: "right" });
    } else {
      doc.text(left, metaRightX, metaY, { align: "right" });
    }
    metaY += i === 0 ? 6 : 5;
  }
  yPos = Math.max(yPos + 6, metaY);
  yPos += 2;
  doc.setLineWidth(0.5);
  doc.setDrawColor(...BLUE_PRIMARY);
  doc.line(tableMargin, yPos, tableRight, yPos);
  yPos += 10;

  const issuerStartY = yPos;
  let leftY = issuerStartY;
  let rightY = issuerStartY;

  if (issuerInfo.logoDataUrl) {
    try {
      doc.addImage(issuerInfo.logoDataUrl, "PNG", tableMargin, leftY - 4, 28, 14);
      leftY += 14;
    } catch {
      /* fall through to text title */
    }
  }

  const title = issuerInfo.title || "Company";
  if (issuerInfo.isDigix && !issuerInfo.logoDataUrl) {
    doc.setFontSize(22);
    doc.setFont("helvetica", "bold");
    const digiPart = "Digi";
    const xleadsPart = "XLeads";
    doc.setTextColor(...DIGI_CYAN);
    doc.text(digiPart, tableMargin, leftY);
    doc.setTextColor(...XLEADS_GRAY);
    doc.text(xleadsPart, tableMargin + doc.getTextWidth(digiPart), leftY);
    leftY += 9;
  } else {
    doc.setFontSize(16);
    doc.setFont("helvetica", "bold");
    doc.setTextColor(...XLEADS_GRAY);
    doc.text(title, tableMargin, leftY);
    leftY += 8;
  }

  defaultTextStyle();
  issuerInfo.lines.forEach((line) => {
    doc.text(line, tableMargin, leftY);
    leftY += 5;
  });

  headingStyle();
  doc.text("Bill To", tableRight, rightY, { align: "right" });
  rightY += 6;
  defaultTextStyle();
  rightY = drawBilledToLines(doc, billedTo, tableRight, rightY, pageWidth);
  yPos = Math.max(leftY, rightY) + 8;

  const headerWidth = pageWidth - tableMargin * 2;
  const colDesc = headerWidth * 0.42;
  const colQty = headerWidth * 0.12;
  const colRate = headerWidth * 0.23;
  const colAmt = headerWidth * 0.23;
  const headerHeight = 7;
  const rowPadding = 2;

  const colQtyRight = tableMargin + colDesc + colQty - 2;
  const colRateRight = tableMargin + colDesc + colQty + colRate - 2;
  const colAmtRight = tableRight - 2;

  const drawTableHeader = (headerY) => {
    doc.setFillColor(...BLUE_PRIMARY);
    doc.rect(tableMargin, headerY, colDesc, headerHeight, "F");
    doc.setFillColor(...BLUE_HEADER_RIGHT);
    doc.rect(tableMargin + colDesc, headerY, headerWidth - colDesc, headerHeight, "F");
    doc.setFontSize(9);
    doc.setFont("helvetica", "bold");
    doc.setTextColor(255, 255, 255);
    doc.text("Service", tableMargin + 4, headerY + headerHeight / 2 + 1.5);
    doc.text("Qty", colQtyRight, headerY + headerHeight / 2 + 1.5, { align: "right" });
    doc.text("Rate", colRateRight, headerY + headerHeight / 2 + 1.5, { align: "right" });
    doc.text("Amount", colAmtRight, headerY + headerHeight / 2 + 1.5, { align: "right" });
  };

  drawTableHeader(yPos);
  yPos += headerHeight + 4;
  defaultTextStyle();

  const rows =
    Array.isArray(lineItems) && lineItems.length > 0
      ? lineItems
      : [{ item: "Service", quantity: 1, rate: 0, amount: 0 }];

  let computedSub = 0;

  rows.forEach((row, idx) => {
    if (yPos > pageHeight - 45) {
      doc.addPage();
      yPos = 20;
      drawWatermark(doc, wm);
      drawTableHeader(yPos);
      yPos += headerHeight + 4;
      defaultTextStyle();
    }

    const qty = Number(row.quantity ?? row.Quantity ?? 0);
    const rate = Number(row.rate ?? row.Unit_Price ?? 0);
    const amount = Number(row.amount != null ? row.amount : qty * rate);
    computedSub += amount;

    const itemText = sanitize(row.item || row.Service_Name || "Service");
    const descExtra = sanitize(row.description || row.Description || "");
    const fullText = descExtra ? `${itemText}\n${descExtra}` : itemText;
    const serviceLines = doc.splitTextToSize(fullText, colDesc - 8);
    const leftHeight = Array.isArray(serviceLines) ? serviceLines.length * 5 : 5;
    const rowHeight = Math.max(leftHeight, 8) + rowPadding * 2;
    const textBaseY = yPos + rowPadding + 2;

    doc.setFont("helvetica", "normal");
    doc.setFontSize(10);
    doc.setTextColor(0, 0, 0);
    doc.text(serviceLines, tableMargin + 4, textBaseY);
    doc.text(String(qty), colQtyRight, textBaseY, { align: "right" });
    doc.text(formatIndianNumber(rate), colRateRight, textBaseY, { align: "right" });
    doc.text(formatIndianNumber(amount), colAmtRight, textBaseY, { align: "right" });

    yPos += rowHeight;
    if (idx < rows.length - 1) {
      doc.setDrawColor(220, 220, 220);
      doc.setLineWidth(0.2);
      doc.line(tableMargin, yPos, tableRight, yPos);
      yPos += 2;
    }
  });

  doc.setDrawColor(0, 0, 0);
  doc.setLineWidth(0.5);
  doc.line(tableMargin, yPos, tableRight, yPos);
  yPos += 8;

  const subTotal = Number(invoice.subTotal ?? invoice.Sub_Total ?? computedSub);
  const taxAmount = Number(invoice.taxAmount ?? invoice.Tax_Amount ?? 0);
  const discount = Number(invoice.discountAmount ?? invoice.Discount_Amount ?? 0);
  const total = Number(invoice.totalAmount ?? invoice.Total_Amount ?? subTotal + taxAmount - discount);
  const paid = Number(invoice.paidAmount ?? invoice.Paid_Amount ?? 0);
  const balance = Number(invoice.balanceAmount ?? invoice.Balance_Amount ?? total - paid);

  const summaryRows = [
    ["Subtotal", subTotal],
    ["Tax", taxAmount],
    ["Discount", discount],
    ["Total", total],
    ["Paid", paid],
    ["Balance", balance]
  ];

  summaryRows.forEach(([label, val], i) => {
    if (yPos > pageHeight - 30) {
      doc.addPage();
      yPos = 20;
      drawWatermark(doc, wm);
      defaultTextStyle();
    }
    const bold = label === "Total" || label === "Balance";
    doc.setFont("helvetica", bold ? "bold" : "normal");
    doc.setFontSize(10);
    doc.setTextColor(0, 0, 0);
    doc.text(label, colRateRight - 20, yPos, { align: "right" });
    doc.text(formatIndianNumber(val), colAmtRight, yPos, { align: "right" });
    yPos += i === 3 ? 7 : 5;
  });

  yPos += 6;

  if (Array.isArray(payments) && payments.length > 0) {
    if (yPos > pageHeight - 40) {
      doc.addPage();
      yPos = 20;
      drawWatermark(doc, wm);
    }
    headingStyle();
    doc.text("Payments", tableMargin, yPos);
    yPos += 6;
    defaultTextStyle();
    payments.forEach((p) => {
      if (yPos > pageHeight - 25) {
        doc.addPage();
        yPos = 20;
        drawWatermark(doc, wm);
        defaultTextStyle();
      }
      const line = [
        sanitize(p.date || p.Payment_Date || ""),
        formatIndianNumber(p.amount ?? p.Amount ?? 0),
        sanitize(p.mode || p.Payment_Mode_Name || ""),
        sanitize(p.ref || p.Transaction_Ref || "")
      ]
        .filter(Boolean)
        .join("  |  ");
      doc.text(line, tableMargin, yPos);
      yPos += 5;
    });
    yPos += 4;
  }

  const notes = sanitize(invoice.notes || invoice.Notes || "");
  if (notes && !notes.startsWith("Billing period:")) {
    if (yPos > pageHeight - 30) {
      doc.addPage();
      yPos = 20;
      drawWatermark(doc, wm);
    }
    headingStyle();
    doc.text("Notes", tableMargin, yPos);
    yPos += 6;
    defaultTextStyle();
    const noteLines = doc.splitTextToSize(notes, headerWidth);
    doc.text(noteLines, tableMargin, yPos);
  }

  return doc;
}

export function downloadInvoicePdf(data, filename) {
  const doc = generateInvoicePdf(data);
  const name = filename || `invoice-${sanitize(data?.invoice?.invoiceNo || "download")}.pdf`;
  doc.save(name.replace(/[^\w.\-]+/g, "_"));
}

/**
 * Payment receipt PDF — same issuer branding as invoices.
 * @param {object} data
 * @param {object} data.payment - { paymentId, paymentDate, amount, currency, mode, ref, notes, status }
 * @param {object} data.invoice - { invoiceNo, invoiceDate, totalAmount, paidAmount, balanceAmount, currency }
 * @param {object} data.billedTo - name, company, contact, email, phone, address, gstin, pan, taxIdType, taxId
 * @param {object} [data.issuer]
 */
export function generatePaymentReceiptPdf(data) {
  const {
    payment = {},
    invoice = {},
    billedTo = {},
    issuer: issuerInput = null
  } = data || {};

  const doc = new jsPDF();
  const pageWidth = doc.internal.pageSize.getWidth();
  const pageHeight = doc.internal.pageSize.getHeight();
  const tableMargin = 20;
  const tableRight = pageWidth - tableMargin;
  const issuerInfo = resolveIssuer(issuerInput);
  const wm = issuerInfo.watermark;

  drawWatermark(doc, wm);

  const defaultTextStyle = () => {
    doc.setFontSize(10);
    doc.setFont("helvetica", "normal");
    doc.setTextColor(0, 0, 0);
  };
  const headingStyle = () => {
    doc.setFontSize(11);
    doc.setFont("helvetica", "bold");
    doc.setTextColor(0, 0, 0);
  };

  let yPos = 20;
  defaultTextStyle();

  doc.setFontSize(14);
  doc.setFont("helvetica", "bold");
  doc.setTextColor(0, 0, 0);
  doc.text("PAYMENT RECEIPT", tableMargin, yPos);
  defaultTextStyle();

  const paymentId = sanitize(payment.paymentId ?? payment.Payment_Id ?? "");
  const paymentDate = sanitize(payment.paymentDate ?? payment.Payment_Date ?? "");
  const invoiceNo = sanitize(invoice.invoiceNo ?? invoice.Invoice_No ?? "");
  const currency = sanitize(
    payment.currency || payment.Currency_Code || invoice.currency || invoice.Currency_Code || ""
  );

  const metaFields = [
    paymentId ? `Receipt: ${paymentId}` : "",
    paymentDate ? `Date: ${paymentDate}` : "",
    invoiceNo ? `Invoice: ${invoiceNo}` : "",
    currency ? `Currency: ${currency}` : ""
  ].filter(Boolean);

  const metaColGap = 6;
  const metaColWidth = 58;
  const metaRightX = tableRight;
  const metaLeftX = tableRight - metaColWidth - metaColGap;
  let metaY = yPos;
  for (let i = 0; i < metaFields.length; i += 2) {
    const left = metaFields[i];
    const right = metaFields[i + 1];
    if (right) {
      doc.text(left, metaLeftX, metaY, { align: "right" });
      doc.text(right, metaRightX, metaY, { align: "right" });
    } else {
      doc.text(left, metaRightX, metaY, { align: "right" });
    }
    metaY += i === 0 ? 6 : 5;
  }
  yPos = Math.max(yPos + 6, metaY) + 2;
  doc.setLineWidth(0.5);
  doc.setDrawColor(...BLUE_PRIMARY);
  doc.line(tableMargin, yPos, tableRight, yPos);
  yPos += 10;

  const issuerStartY = yPos;
  let leftY = issuerStartY;
  let rightY = issuerStartY;

  if (issuerInfo.logoDataUrl) {
    try {
      doc.addImage(issuerInfo.logoDataUrl, "PNG", tableMargin, leftY - 4, 28, 14);
      leftY += 14;
    } catch {
      /* text title */
    }
  }

  const title = issuerInfo.title || "Company";
  if (issuerInfo.isDigix && !issuerInfo.logoDataUrl) {
    doc.setFontSize(22);
    doc.setFont("helvetica", "bold");
    const digiPart = "Digi";
    const xleadsPart = "XLeads";
    doc.setTextColor(...DIGI_CYAN);
    doc.text(digiPart, tableMargin, leftY);
    doc.setTextColor(...XLEADS_GRAY);
    doc.text(xleadsPart, tableMargin + doc.getTextWidth(digiPart), leftY);
    leftY += 9;
  } else {
    doc.setFontSize(16);
    doc.setFont("helvetica", "bold");
    doc.setTextColor(...XLEADS_GRAY);
    doc.text(title, tableMargin, leftY);
    leftY += 8;
  }

  defaultTextStyle();
  issuerInfo.lines.forEach((line) => {
    doc.text(line, tableMargin, leftY);
    leftY += 5;
  });

  headingStyle();
  doc.text("Received From", tableRight, rightY, { align: "right" });
  rightY += 6;
  defaultTextStyle();
  rightY = drawBilledToLines(doc, billedTo, tableRight, rightY, pageWidth);
  yPos = Math.max(leftY, rightY) + 10;

  const amount = Number(payment.amount ?? payment.Amount ?? 0);
  const mode = sanitize(payment.mode || payment.Payment_Mode_Name || "");
  const ref = sanitize(payment.ref || payment.Transaction_Ref || "");
  const status = sanitize(payment.status || payment.Payment_Status || "Completed");

  const boxH = 22;
  doc.setFillColor(232, 244, 253);
  doc.setDrawColor(...BLUE_PRIMARY);
  doc.setLineWidth(0.4);
  doc.roundedRect(tableMargin, yPos, pageWidth - tableMargin * 2, boxH, 2, 2, "FD");
  doc.setFontSize(10);
  doc.setFont("helvetica", "normal");
  doc.setTextColor(71, 85, 105);
  doc.text("Amount received", tableMargin + 8, yPos + 8);
  doc.setFontSize(16);
  doc.setFont("helvetica", "bold");
  doc.setTextColor(...BLUE_PRIMARY);
  const amountLabel = `${currency ? `${currency} ` : ""}${formatIndianNumber(amount)}`.trim();
  doc.text(amountLabel, tableRight - 8, yPos + 14, { align: "right" });
  yPos += boxH + 12;

  const detailRows = [
    ["Payment mode", mode || "—"],
    ["Transaction ref", ref || "—"],
    ["Status", status || "—"],
    ["Invoice total", formatIndianNumber(Number(invoice.totalAmount ?? invoice.Total_Amount ?? 0))],
    ["Invoice paid (incl. this)", formatIndianNumber(Number(invoice.paidAmount ?? invoice.Paid_Amount ?? amount))],
    ["Invoice balance", formatIndianNumber(Number(invoice.balanceAmount ?? invoice.Balance_Amount ?? 0))]
  ];

  headingStyle();
  doc.text("Payment details", tableMargin, yPos);
  yPos += 7;
  defaultTextStyle();

  detailRows.forEach(([label, value]) => {
    if (yPos > pageHeight - 28) {
      doc.addPage();
      yPos = 20;
      drawWatermark(doc, wm);
      defaultTextStyle();
    }
    doc.setFont("helvetica", "normal");
    doc.setTextColor(100, 116, 139);
    doc.text(label, tableMargin, yPos);
    doc.setTextColor(0, 0, 0);
    doc.setFont("helvetica", "bold");
    doc.text(String(value), tableRight, yPos, { align: "right" });
    yPos += 6;
  });

  const notes = sanitize(payment.notes || payment.Notes || "");
  if (notes) {
    yPos += 4;
    if (yPos > pageHeight - 36) {
      doc.addPage();
      yPos = 20;
      drawWatermark(doc, wm);
    }
    headingStyle();
    doc.text("Notes", tableMargin, yPos);
    yPos += 6;
    defaultTextStyle();
    const noteLines = doc.splitTextToSize(notes, pageWidth - tableMargin * 2);
    doc.text(noteLines, tableMargin, yPos);
  }

  return doc;
}

export function downloadPaymentReceiptPdf(data, filename) {
  const doc = generatePaymentReceiptPdf(data);
  const invNo = sanitize(data?.invoice?.invoiceNo || data?.invoice?.Invoice_No || "payment");
  const payId = sanitize(data?.payment?.paymentId || data?.payment?.Payment_Id || "");
  const name = filename || `receipt-${invNo}${payId ? `-${payId}` : ""}.pdf`;
  doc.save(name.replace(/[^\w.\-]+/g, "_"));
}

/** Load image URL to data URL for jsPDF (fails quietly on CORS). */
export function loadImageAsDataUrl(url) {
  return new Promise((resolve) => {
    if (!url) {
      resolve(null);
      return;
    }
    const img = new Image();
    img.crossOrigin = "anonymous";
    img.onload = () => {
      try {
        const canvas = document.createElement("canvas");
        canvas.width = img.naturalWidth || img.width;
        canvas.height = img.naturalHeight || img.height;
        const ctx = canvas.getContext("2d");
        ctx.drawImage(img, 0, 0);
        resolve(canvas.toDataURL("image/png"));
      } catch {
        resolve(null);
      }
    };
    img.onerror = () => resolve(null);
    img.src = url;
  });
}
